<template>
  <div class="file-uploader-custom" :class="{ 'is-disabled': disabled }">
    <div v-if="label" class="file-uploader-label mb-3">
      {{ label }}
    </div>

    <div class="file-uploader-container d-flex flex-wrap align-items-start mb-0">
      <!-- Upload Trigger -->
      <div
        v-if="showUploadTrigger"
        class="upload-trigger-box mr-3 mb-2"
        :class="{ 'is-invalid': isInvalid, 'is-disabled': disabled }"
        @click="triggerFileInput"
      >
        <input
          ref="fileInput"
          type="file"
          class="d-none"
          :accept="acceptAttribute"
          :disabled="disabled"
          @change="handleFileChange"
        />
        <div class="upload-icon">
          <i class="icon-fill_upload"></i>
        </div>
      </div>

      <!-- Placeholder when no files and uploader is hidden -->
      <div
        v-if="!showUploadTrigger && uploadedFiles.length === 0"
        class="uploaded-file-item mr-3 mb-2"
      >
        <div class="file-preview-box is-disabled">
          <div class="file-icon-box">
            <i class="icon-outline_file"></i>
          </div>
        </div>
      </div>

      <!-- Uploaded Files List -->
      <div
        v-for="(file, index) in uploadedFiles"
        :key="index"
        class="uploaded-file-item mr-3 mb-2"
      >
        <div class="file-preview-box" :class="{ 'is-disabled': disabled }" @click="handlePreview(file, index)">
          <img v-if="isImage(file)" :src="getFileUrl(file)" class="preview-img" />
          <div v-else class="file-icon-box">
            <i class="icon-outline_file"></i>
          </div>
        </div>
        <button
          v-if="!readonly && !disabled"
          class="remove-file-btn"
          @click.stop="removeFile(index)"
        >
          <i class="icon-outline_close"></i>
        </button>
        <button
          v-else-if="readonly"
          class="download-file-btn"
          @click.stop="handleDownload(getFileUrl(file), file.name || file.file_name)"
        >
          <i class="icon-outline_download"></i>
        </button>
        <div class="file-name text-truncate" :title="file.name || file.file_name">
          {{ file.name || file.file_name }}
        </div>
      </div>
    </div>

    <McShowImage
      :visible.sync="showPreview"
      :attachments="uploadedFiles"
      :activeIndex.sync="activeIndex"
      @download="handleDownload"
    />

    <div class="validation-info" :class="{ 'is-invalid': isInvalid }">
      <div v-if="isRequired" class="required-text mb-1">
        <span class="text-danger">*</span>
        <span class="text-muted italic-label">Wajib diisi</span>
      </div>
      <div v-if="showAllowedFormats" class="help-text text-muted mt-2">
        (Format {{ formattedAllowedFormats }} • Maks. {{ maxSize }} MB)
      </div>
      <div v-if="error" class="error-text text-danger mt-1">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import McShowImage from '@/components/McShowImage'

export default {
  components: {
    McShowImage,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    maxSize: {
      type: Number,
      default: 2, // in MB
    },
    allowedFormats: {
      type: Array,
      default: () => ['.jpeg', '.jpg', '.png', '.pdf', '.docx'],
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showAllowedFormats: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      error: '',
      showPreview: false,
      activeIndex: 0,
    }
  },
  computed: {
    uploadedFiles() {
      // If it's multiple, context.model should be an array
      if (this.multiple) {
        return Array.isArray(this.context.model) ? this.context.model : []
      }
      // If single, context.model is an object or null
      // Check if it's an empty array (often default value) or null/undefined
      if (!this.context.model || (Array.isArray(this.context.model) && this.context.model.length === 0)) {
        return []
      }
      return [this.context.model]
    },
    canUploadMore() {
      if (this.readonly || this.disabled) return false
      if (!this.multiple) {
        return this.uploadedFiles.length === 0
      }
      return this.uploadedFiles.length < this.maxFiles
    },
    showUploadTrigger() {
      return this.canUploadMore || (this.disabled && !this.readonly && this.uploadedFiles.length === 0)
    },
    acceptAttribute() {
      return this.allowedFormats.join(',')
    },
    formattedAllowedFormats() {
      return this.allowedFormats.join(', ')
    },
    isRequired() {
      // Prioritize props.required, but also check context validation
      return this.required || (this.context.validation && this.context.validation.includes('required'))
    },
    isInvalid() {
      // Don't show validation error in readonly mode
      if (this.readonly) return false

      // Return true only if Formulate explicitly says we should show validation errors
      // and there are actually errors present.
      return this.context.showValidationErrors && this.context.visibleValidationErrors.length > 0
    },
  },
  methods: {
    triggerFileInput() {
      if (this.disabled) return
      this.$refs.fileInput.click()
    },
    async handleFileChange(event) {
      if (this.disabled) return
      const file = event.target.files[0]
      if (!file) return

      this.error = ''

      // Validation: Size
      const maxBytes = this.maxSize * 1024 * 1024
      if (file.size > maxBytes) {
        this.error = `Ukuran file maksimal ${this.maxSize} MB`
        event.target.value = ''
        return
      }

      // Validation: Format
      const fileExt = '.' + file.name.split('.').pop().toLowerCase()
      if (!this.allowedFormats.includes(fileExt)) {
        this.error = `Format file tidak didukung. Gunakan: ${this.formattedAllowedFormats}`
        event.target.value = ''
        return
      }

      try {
        const base64Data = await this.toBase64(file)
        const base64Only = base64Data.split(',')[1]

        const formData = new FormData()
        formData.append('file', file)

        const fileData = {
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_base64: base64Only,
          url: URL.createObjectURL(file), // for preview
          _isNew: true, // flag for internal use
          file: file,
          formData: formData,
        }

        if (this.multiple) {
          const newFiles = [fileData, ...this.uploadedFiles]
          this.context.model = newFiles
        } else {
          this.context.model = fileData
        }

        this.$emit('file-selected', fileData)
        
        // Reset input so same file can be selected again if removed
        event.target.value = ''
      } catch (err) {
        console.error('Error processing file:', err)
        this.error = 'Terjadi kesalahan saat memproses file.'
      }
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    removeFile(index) {
      if (this.disabled) return
      let removedFile = null
      if (this.multiple) {
        const newFiles = [...this.uploadedFiles]
        removedFile = newFiles.splice(index, 1)[0]
        if (removedFile.url && removedFile._isNew) {
          URL.revokeObjectURL(removedFile.url)
        }
        this.context.model = newFiles
      } else {
        removedFile = this.context.model
        if (removedFile && removedFile.url && removedFile._isNew) {
          URL.revokeObjectURL(removedFile.url)
        }
        this.context.model = null
      }

      if (removedFile) {
        this.$emit('file-removed', removedFile)
      }
    },
    isImage(file) {
      const fileName = file.name || file.file_name || ''
      const ext = fileName.split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
    },
    getFileUrl(file) {
      return file.url || file.file_url || ''
    },
    handlePreview(file, index) {
      if (this.disabled) return
      if (this.isImage(file)) {
        this.activeIndex = index
        this.showPreview = true
      } else {
        const url = this.getFileUrl(file)
        if (url) {
          window.open(url, '_blank')
        }
      }
    },
    async handleDownload(url, fileName) {
      if (this.disabled) return
      try {
        const response = await fetch(url)
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName || 'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Cleanup blob URL
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('Download failed:', error)
        // Fallback to old method if fetch fails (e.g. CORS issues)
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.download = fileName
        link.click()
      }
    },
  },
}
</script>

<style lang="scss">
.formulate-input[data-type="file-uploader-custom"] {
  .formulate-input-errors {
    display: none !important;
  }
}
</style>

<style lang="scss" scoped>
.file-uploader-custom {
  .file-uploader-label {
    font-size: 1.1rem;
    font-weight: 400;
    color: #475467;
    line-height: 1.5;
  }

  .upload-trigger-box {
    width: 100px;
    height: 100px;
    border: 1px dashed #98a2b3;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &.is-invalid {
      border-color: #f04438;
      border-style: dashed;
    }

    &.is-disabled {
      border-color: #d0d5dd;
      background-color: #CFDAE9;
      cursor: not-allowed;
      pointer-events: none;
    }

    &:hover {
      border-color: #667085;
      background-color: #f9fafb;
    }

    &.is-disabled:hover {
      border-color: #d0d5dd;
      background-color: #CFDAE9;
    }

    .upload-icon {
      i {
        font-size: 24px;
        color: #667085;
      }
    }

    &.is-disabled .upload-icon i {
      color: #98a2b3;
    }
  }

  .uploaded-file-item {
    width: 100px;
    flex-shrink: 0; // Pastikan item tidak mengecil/tertumpuk
    position: relative; // Tambahkan ini sebagai konteks tambahan
    
    .file-preview-box {
        width: 100px;
        height: 100px;
        border: 1px solid #eaecf0;
        border-radius: 4px;
        position: relative;
        overflow: hidden;
        background-color: #f9fafb;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        aspect-ratio: 1 / 1;
        cursor: pointer;

        &.is-disabled {
          cursor: not-allowed;
          pointer-events: none;
          opacity: 0.75;
        }

        .preview-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

      .file-icon-box {
        i {
          font-size: 24px;
          color: #667085;
        }
      }
    }

    .remove-file-btn {
      position: absolute;
      top: -8px;
      right: -8px;
      background: #f64a33;
      border: 2px solid #ffffff;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      z-index: 1;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      
      i {
        color: #ffffff;
        font-size: 14px;
        font-weight: bold;
      }

      &:hover {
        background: #d93d26;
      }
    }

    .download-file-btn {
      position: absolute;
      top: 3px;
      right: 3px;
      background: #ffffff;
      border: 1px solid #eaecf0;
      border-radius: 3px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      z-index: 1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      
      i {
        color: #1c325e;
        font-size: 18px;
      }

      &:hover {
        background: #f9fafb;
      }
    }

    .file-name {
      font-size: 11px;
      color: #475467;
      margin-top: 8px;
      text-align: center;
    }
  }

  .file-uploader-container {
    padding-top: 12px; // Berikan ruang lebih untuk tombol delete yang melayang
    overflow: visible; // Pastikan tombol delete tidak terpotong
  }

  .validation-info {
    .required-text {
      font-size: 14px;
      display: flex;
      align-items: center;
      
      .text-danger {
        color: #f04438 !important;
        font-size: 16px;
      }

      .italic-label {
        color: #667085 !important;
        font-style: italic;
        margin-left: 6px;
        font-size: 12px;
        margin-bottom: 5px;
      }
    }

    &.is-invalid {
      .required-text {
        .italic-label {
          color: #f04438 !important;
        }
      }
    }

    .help-text {
      font-size: 14px;
      color: #667085 !important;
    }
  }
}
</style>
